/** @jsx jsx */
import { jsx } from '@emotion/react'
import React, { useState, useEffect, useContext } from 'react'
import styled from '@emotion/styled'
import { isMobile } from 'react-device-detect'

import H3 from '../typography/H3'
import useEscKeyContext from '../hookforms/framework/hooks/useEscKeyContext'
import { ModalsContext } from '../modals/Modals'
import IconClose from '../svg/IconClose'

const H3AsH1 = H3.withComponent('h1')

const BORDER_RADIUS = 8
const CONTENT_PADDING = '2em'

const Container = styled.div(({ theme }) => ({
  alignItems: 'flex-start',
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  left: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: '2em 0',
  position: 'fixed',
  right: 0,
  top: 0,
  zIndex: theme.zIndexes.modalContainer,
}))

const ModalHeader = styled.div(({ theme }) => ({
  borderTopLeftRadius: `${BORDER_RADIUS}px`,
  borderTopRightRadius: `${BORDER_RADIUS}px`,
  backgroundColor: theme.colors.iconGray,
  padding: `15px ${CONTENT_PADDING}`,
}))

interface DarkBackgroundProps {
  fadeIn?: boolean
  onClick: () => void
}

const DarkBackground = styled.div<DarkBackgroundProps>(({ fadeIn }) => ({
  backgroundColor: 'rgba(0,0,0,0.8)',
  bottom: '0px',
  left: '0px',
  opacity: fadeIn === true ? 1.0 : 0,
  position: 'absolute',
  right: '0px',
  top: '0px',
  transition: 'opacity 0.2s ease-in',
  zIndex: 0,
  willChange: 'opacity',
}))

interface ModalProps {
  backgroundColor?: string
  fadeIn?: boolean
  size: number
  fillScreen?: boolean
}

const Modal = styled.div<ModalProps>(
  ({ theme, fadeIn, size, backgroundColor }) => ({
    backgroundColor: backgroundColor ? backgroundColor : 'white',
    borderRadius: `${BORDER_RADIUS}px`,
    color: theme.colors.grays.grayDark,
    fontFamily: theme.fonts.families.body,
    height: 'auto',
    marginLeft: 'auto',
    marginRight: 'auto',
    maxHeight: 'calc(100vh - 4em)',
    maxWidth: '90%',
    opacity: fadeIn === true ? 1 : 0,
    overflowY: 'auto',
    position: 'relative',
    transform: fadeIn === true ? 'translateY(0)' : 'translateY(24px)',
    transition: 'opacity 0.2s ease-in, transform 0.2s ease-out',
    willChange: 'opacity, transform',
    width: `${size}px`,
    zIndex: theme.zIndexes.modalContainer,
  }),
  ({ fillScreen }) =>
    !!fillScreen && {
      margin: 0,
      padding: '3% 8%',
      borderRadius: 0,
      maxWidth: '100%',
      height: '100%',
      maxHeight: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }
)

export interface Props {
  backgroundColor?: string
  backgroundClose?: boolean
  canClose?: boolean
  dataTest?: string
  /** Set this to `true` if you want to disable the ESC key for this modal */
  disableEsc?: boolean
  isVisible?: boolean
  mobileFull?: boolean
  onClose?: () => void
  size?: number
  title?: string
  children?: React.ReactNode
}

const ModalFullscreen: React.FC<Props> = (props) => {
  const {
    backgroundColor,
    backgroundClose = true,
    canClose = true,
    children,
    dataTest,
    disableEsc,
    isVisible,
    mobileFull = false,
    onClose,
    size = 600,
    title,
  } = props
  const [fadeIn, setFadeIn] = useState(false)
  const modalsCtx = useContext(ModalsContext)
  const escCtx = useEscKeyContext()

  function handleClose() {
    if (!canClose) {
      return
    }

    if (!!onClose && !!canClose) {
      onClose()
      return
    }
    setFadeIn(false)
    if (!!modalsCtx && !!canClose) {
      modalsCtx.hide()
    }
  }

  if (!isVisible) {
    return null
  }

  useEffect(() => {
    if (!disableEsc) {
      escCtx.push('modal', handleClose)
    }
    if (isVisible === true) {
      window.requestAnimationFrame(() => {
        setFadeIn(true)
      })
    }
    return function cleanup() {
      if (!disableEsc) {
        escCtx.pop('modal')
      }
    }
  }, [])

  return (
    <Container data-test={dataTest}>
      <DarkBackground
        data-test="ModalFullscreen-DarkBackground"
        fadeIn={fadeIn}
        onClick={() => !!backgroundClose && handleClose()}
      />
      <Modal
        data-test="ModalFullscreen"
        fadeIn={fadeIn}
        size={size}
        backgroundColor={backgroundColor}
        fillScreen={isMobile && !!mobileFull}
        onClick={(e) => e.stopPropagation()}
      >
        {(!isMobile || !mobileFull) && (
          <React.Fragment>
            {backgroundColor !== 'transparent' && (
              <ModalHeader css={{ position: 'relative' }}>
                {!!title && <H3AsH1 css={{ margin: '0px' }}>{title}</H3AsH1>}
                <div
                  css={{
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    top: '0px',
                    right: '15px',
                    height: '100%',
                  }}
                  onClick={() => handleClose()}
                >
                  <IconClose variant="white" width="10px" />
                </div>
              </ModalHeader>
            )}
            <div css={{ padding: CONTENT_PADDING }}>{children}</div>
          </React.Fragment>
        )}
        {isMobile && !!mobileFull && (
          <React.Fragment>
            <div
              role="presentation"
              css={{
                position: 'absolute',
                lineHeight: '1',
                zIndex: 5,
                right: '15px',
                top: '20px',
              }}
              onClick={() => handleClose()}
            >
              <IconClose variant="primary" width="12px" />
            </div>
            {children}
          </React.Fragment>
        )}
      </Modal>
    </Container>
  )
}

export default ModalFullscreen
